/* Core & libraries */
import React, { useEffect } from "react";

/* Helpers, constants, custom methods, internal libraries */
import { useGenericApiFetch } from "../endpoints/base";
import {
  ASSOCIATE_LABEL_ENDPOINT,
  CREATE_LABEL_ENDPOINT,
  DELETE_LABEL_ENDPOINT,
  GET_LABELS_ENDPOINT,
  UNASSIGN_LABEL_ENDPOINT,
  UPDATE_LABEL_ENDPOINT,
} from "../endpoints/endpoints";
import { FormattedMessage } from "react-intl";
import { useNotificationContext } from "../contexts/notificationContext";
import { useUserContext } from "../contexts/userContext";

/* Components */
/* Styles */

export const useLabelService = () => {
  const { genericApiFetch } = useGenericApiFetch();
  const { setNotification } = useNotificationContext();
  const userContext = useUserContext();
  const companyEntityId = userContext?.userInfo?.companyInfo?.companyEntityId;
  
  const getLabels = async (payload) => {
    
    const response = await genericApiFetch({
      url: GET_LABELS_ENDPOINT.replace('{companyEntityId}', companyEntityId),
      method: "GET",
      queryParams: { searchTerm: payload },
      spinner: "getLabels",
    });
    
    if (response.success) {
      return response.data?.labels
    }
    return false
  };
  
  const createLabel = async (payload) => {
    const response = await genericApiFetch({
      url: CREATE_LABEL_ENDPOINT.replace('{companyEntityId}', companyEntityId),
      method: "POST",
      data: payload,
      spinner: "getLabels"
    });
    
    if (response.success) {
      return response.data
    } else if (response?.errorMessages[0]?.code === "12002") {
      setNotification(
        "error",
        <FormattedMessage id="labelService.alreadyExistTitle"/>,
        <FormattedMessage id="labelService.alreadyExistText"/>
      );
      return false
    }
    setNotification(
      "error",
      <FormattedMessage id="labelService.errorTitle"/>,
      <FormattedMessage id="labelService.errorText"/>
    );
    return false
  };
  
  const updateLabel = async (payload) => {
    const response = await genericApiFetch({
      url: UPDATE_LABEL_ENDPOINT.replace('{companyEntityId}', companyEntityId).replace('{labelId}', payload?.labelId),
      method: "PATCH",
      data: payload,
      spinner: "editLabelSpinner"
    });
    
    if (response.success) {
      return response.data
    } else if (response?.errorMessages[0]?.code === "12002") {
      return response;
    }
    
    setNotification(
      "error",
      <FormattedMessage id="labelService.errorTitle"/>,
      <FormattedMessage id="labelService.errorText"/>
    );
    return false
  };
  
  const associateLabel = async (payload) => {
    const response = await genericApiFetch({
      url: ASSOCIATE_LABEL_ENDPOINT.replace('{companyEntityId}', companyEntityId).replace('{userEntityId}', payload.userEntityId),
      method: "PUT",
      data: { labelId: payload?.labelId || "", label: payload?.label || "" },
      spinner: "getLabels"
    });
    
    if (response.success) {
      return response.data
    }
    return false
  };
  
  const deleteLabel = async (labelId) => {
    const response = await genericApiFetch({
      url: DELETE_LABEL_ENDPOINT.replace('{companyEntityId}', companyEntityId).replace('{labelId}', labelId),
      method: "DELETE",
      spinner: "deleteActionSpinner",
    });
    
    if (response.success) {
      return true;
    }
    setNotification(
      "error",
      <FormattedMessage id="labelService.errorTitle"/>,
      <FormattedMessage id="labelService.errorText"/>
    );
    return false
  };
  
  const unassignLabel = async (payload) => {
    const response = await genericApiFetch({
      url: UNASSIGN_LABEL_ENDPOINT
        .replace('{companyEntityId}', companyEntityId)
        .replace('{userEntityId}', payload.userEntityId)
        .replace('{labelId}', payload.labelId),
      method: "DELETE",
      spinner: "getLabels",
    });
    
    if (response.success) {
      return true;
    }
    setNotification(
      "error",
      <FormattedMessage id="labelService.errorTitle"/>,
      <FormattedMessage id="labelService.errorText"/>
    );
    return false
  };
  
  return {
    getLabels,
    createLabel,
    updateLabel,
    associateLabel,
    deleteLabel,
    unassignLabel
  };
}

